// src/components/shop/ShopGridClient.tsx

"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import Section from "@/components/ui/Section";
import Wrapper from "@/components/ui/Wrapper";
import SafeHtml from "@/components/ui/SafeHtml";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";
import {
  type FooEvents,
  type FooEventsSlots,
  getEventDateLabel as getEventDateLabelShared,
  getEventStart,
  isUpcoming,
} from "@/lib/eventDateHelpers";

const BASE = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
const PAGE_SIZE = 24;

export type ProductCat = { id: number; slug: string; name: string; count: number };

type FeaturedMedia = {
  alt_text?: string;
  source_url?: string;
  code?: string;
  media_details?: {
    sizes?: {
      medium_large?: { source_url?: string };
      medium?: { source_url?: string };
      [key: string]: { source_url?: string } | undefined;
    };
  };
};

type RenderedField = { rendered?: string };

export type Product = {
  id: number;
  slug: string;
  link?: string;
  featured_image?: string | null;
  title?: RenderedField;
  excerpt?: RenderedField;
  content?: RenderedField;
  _embedded?: { "wp:featuredmedia"?: FeaturedMedia[] };
  fooevents?: FooEvents | null;
  fooevents_slots?: FooEventsSlots | null;
  price_amount?: number | null;
  is_free?: boolean;
  stock_status?: string;
  stock_quantity?: number | null;
  tickets_left?: number | null;
};

interface Props {
  items: Product[];
  basePath: string;
  lang: string;
  categories: ProductCat[];
  selectedCatSlug: string;
}

type RibbonSpec = { label: string; modifier?: string } | null;

function resolveLocale(lang: string): string {
  if (lang === "pt") return "pt-PT";
  if (lang === "es") return "es-ES";
  return lang || "pt-PT";
}

function pageWindow(current: number, total: number, size = 5) {
  if (total <= size) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(size / 2);
  let start = current - half;
  let end = current + half;
  if (start < 1) { start = 1; end = size; }
  if (end > total) { end = total; start = total - size + 1; }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function withBase(u?: string | null): string | null {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  if (!BASE) return u.startsWith("/") ? u : `/${u}`;
  const normalized = u.startsWith("/") ? u : `/${u}`;
  const originUrl = new URL(BASE);
  const originPath = originUrl.pathname.replace(/\/$/, "");
  if (originPath && normalized.startsWith(originPath + "/")) {
    return originUrl.origin + normalized;
  }
  return `${BASE}${normalized}`;
}

function getImageSrc(product: Product): string | null {
  const media = product._embedded?.["wp:featuredmedia"]?.[0];
  if (media && !("code" in media && media.code)) {
    const src =
      media.media_details?.sizes?.medium_large?.source_url ||
      media.media_details?.sizes?.medium?.source_url ||
      media.source_url;
    if (src) return withBase(src);
  }
  if (product.featured_image) return withBase(product.featured_image);
  return null;
}

function getHref(product: Product): string {
  if (typeof product.link === "string" && product.link) {
    try {
      return new URL(product.link, "https://example.local").pathname;
    } catch { /* fall */ }
  }
  return `/${product.slug}`;
}

function stripHtml(s = ""): string {
  return s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function decodeWpEntities(s: string): string {
  return s
    .replace(/&#(\d+);/g, (_, c) => String.fromCharCode(Number(c)))
    .replace(/&amp;/g, "&")
    .replace(/&ndash;/g, "–")
    .replace(/&#8211;/g, "–");
}

function getTitle(product: Product): string {
  return decodeWpEntities(stripHtml(product.title?.rendered || ""));
}

function truncateWords(text: string, limit = 20): string {
  const words = text.trim().split(/\s+/);
  if (words.length <= limit) return text;
  return words.slice(0, limit).join(" ") + "…";
}

function getExcerptLike(product: Product): { html?: string; generated?: string } {
  const excerptHtml = (product.excerpt?.rendered || "").trim();
  if (excerptHtml) return { html: excerptHtml };
  const text = stripHtml((product.content?.rendered || "").trim());
  if (!text) return {};
  return { generated: truncateWords(text, 20) };
}

function formatPrice(amount: number): string {
  try {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `€${amount}`;
  }
}

function getEventDateLabel(product: Product, lang: string): string {
  const locale = resolveLocale(lang);
  return getEventDateLabelShared(
    product,
    locale,
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    },
    { compact: true },
  );
}

function getRibbonSpec(
  product: Product,
  eventDateLabel: string,
  labels: { free: string; soldOut: string; past: string }
): RibbonSpec {
  if (eventDateLabel && !isUpcoming(product)) {
    return { label: labels.past, modifier: "price-ribbon--past" };
  }
  const soldOut =
    product.stock_status === "outofstock" ||
    (typeof product.tickets_left === "number" && product.tickets_left <= 0);
  if (soldOut) {
    return { label: labels.soldOut, modifier: "price-ribbon--sold-out" };
  }
  if (product.is_free === true) {
    return { label: labels.free };
  }
  const amount = typeof product.price_amount === "number" ? product.price_amount : NaN;
  if (!isNaN(amount) && amount > 0) {
    return { label: formatPrice(amount) };
  }
  return null;
}

function buildUrl(basePath: string, params: Record<string, string | number | undefined>) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
  });
  const q = qs.toString();
  return q ? `${basePath}?${q}` : basePath;
}

export default function ShopGridClient({
  items: ssrItems,
  basePath,
  lang,
  categories,
  selectedCatSlug,
}: Props) {
  const { company_data: companyData } = useLayoutData();
  const SHOP: Record<string, string> =
    (companyData?.globalText as { shop?: Record<string, string> } | undefined)?.shop ?? {};
  const t = (k: string, fallback = "") => SHOP[k] || fallback;

  const [products, setProducts] = React.useState<Product[]>(ssrItems);
  const [cats, setCats] = React.useState<ProductCat[]>(categories);
  const [activeCat, setActiveCat] = React.useState<string>(selectedCatSlug || "all");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);

  const abortRef = React.useRef<AbortController | null>(null);
  const catsAbortRef = React.useRef<AbortController | null>(null);

  const trackRef = React.useRef<HTMLUListElement | null>(null);
  const tabRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});
  const [slider, setSlider] = React.useState({ left: 0, top: 0, width: 0, height: 0 });

  const gridTopRef = React.useRef<HTMLDivElement | null>(null);
  const shouldScrollRef = React.useRef(false);

  const scrollToGridWithOffset = React.useCallback(() => {
    const el = gridTopRef.current;
    if (!el) return;
    const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize || "16");
    const offset = 66 + 2 * rootFont;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  React.useEffect(() => {
    if (!shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    scrollToGridWithOffset();
  }, [page, scrollToGridWithOffset]);

  const measureOnce = React.useCallback(
    (slug: string) => {
      const track = trackRef.current;
      const tab = tabRefs.current[slug];
      if (!track || !tab) return;
      const tb = tab.getBoundingClientRect();
      const tr = track.getBoundingClientRect();
      const next = { left: tb.left - tr.left, top: tb.top - tr.top, width: tb.width, height: tb.height };
      const diff =
        Math.abs(next.left - slider.left) + Math.abs(next.top - slider.top) +
        Math.abs(next.width - slider.width) + Math.abs(next.height - slider.height);
      if (diff > 0.5) setSlider(next);
    },
    [slider.left, slider.top, slider.width, slider.height]
  );

  const rafId = React.useRef<number | null>(null);
  const debounceId = React.useRef<number | null>(null);

  const queueRemeasure = React.useCallback(
    (slug?: string) => {
      const target = slug || activeCat;
      if (rafId.current) { cancelAnimationFrame(rafId.current); rafId.current = null; }
      if (debounceId.current) { window.clearTimeout(debounceId.current); debounceId.current = null; }
      debounceId.current = window.setTimeout(() => {
        rafId.current = requestAnimationFrame(() => {
          Promise.resolve().then(() => measureOnce(target));
        });
      }, 80);
    },
    [activeCat, measureOnce]
  );

  React.useEffect(() => { queueRemeasure(activeCat); }, [activeCat, queueRemeasure]);

  React.useEffect(() => {
    const onResize = () => queueRemeasure(activeCat);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [activeCat, queueRemeasure]);

  React.useEffect(() => {
    const fonts = (document as unknown as { fonts?: { ready?: Promise<void> } }).fonts;
    if (fonts?.ready) fonts.ready.then(() => queueRemeasure(activeCat));
  }, [activeCat, queueRemeasure]);

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const ro = new ResizeObserver(() => queueRemeasure(activeCat));
    ro.observe(track);
    Object.values(tabRefs.current).forEach((el) => el && ro.observe(el));
    const mo = new MutationObserver(() => queueRemeasure(activeCat));
    mo.observe(track, { subtree: true, characterData: true, childList: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [activeCat, queueRemeasure]);

  React.useEffect(() => {
    setActiveCat(selectedCatSlug || "all");
    setProducts(ssrItems);
    setPage(1);
    queueRemeasure(selectedCatSlug || "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCatSlug, ssrItems]);

  React.useEffect(() => {
    if (!BASE) return;
    catsAbortRef.current?.abort();
    const ac = new AbortController();
    catsAbortRef.current = ac;
    fetch(
      `${BASE}/wp-json/wp/v2/product_cat?lang=${encodeURIComponent(lang)}&per_page=50&hide_empty=1`,
      { headers: { Accept: "application/json" }, signal: ac.signal }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (Array.isArray(data)) setCats(data as ProductCat[]);
      })
      .catch(() => { /* keep SSR cats on failure */ });
    return () => ac.abort();
  }, [lang]);

  const fetchFiltered = React.useCallback(
    async (catSlug: string) => {
      if (!BASE) return;
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      try {
        let url =
          `${BASE}/wp-json/bright/v1/products?` +
          `lang=${encodeURIComponent(lang)}&per_page=100&_embed=1&status=publish&include_fooevents=1`;
        if (catSlug !== "all") {
          const cat = cats.find((c) => c.slug === catSlug);
          if (cat?.id) url += `&product_cat=${cat.id}`;
        }
        const res = await fetch(url, { headers: { Accept: "application/json" }, signal: ac.signal });
        if (!res.ok) { setProducts([]); return; }
        const data = (await res.json()) as unknown;
        setProducts(Array.isArray(data) ? (data as Product[]) : []);
      } catch (e) {
        const err = e as { name?: string };
        if (err.name !== "AbortError") setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    [lang, cats]
  );

  React.useEffect(() => {
    if (ssrItems.length === 0) fetchFiltered(selectedCatSlug || "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const updateUrl = React.useCallback(
    (params: Record<string, string | number | undefined>) => {
      if (typeof window === "undefined") return;
      const url = buildUrl(basePath, params);
      window.history.replaceState({}, "", `${window.location.origin}${url}`);
    },
    [basePath]
  );

  const selectCat = (slug: string) => {
    if (slug === activeCat) return;
    setActiveCat(slug);
    setPage(1);
    updateUrl({ cat: slug !== "all" ? slug : undefined });
    fetchFiltered(slug);
    queueRemeasure(slug);
  };

  const setTabRef = (slug: string) => (el: HTMLButtonElement | null) => {
    tabRefs.current[slug] = el;
  };

  const visibleCats = React.useMemo(
    () => cats.filter((c) => c.count > 0 && c.slug !== "uncategorized"),
    [cats]
  );
  const allLabel = t("all_option_label", "Todos");

  const tabItems = React.useMemo(
    () => [{ slug: "all", name: allLabel }, ...visibleCats.map((c) => ({ slug: c.slug, name: c.name }))], 
    [visibleCats, allLabel]
  );

  /**
   * Sort products by event start date (falls back to start), most recent first.
   * Currently-running ranges float to the top; non-events sink to the end.
   * Uses getEventEnd from eventDateHelpers, which handles all four FooEvents
   * kinds (single, sequential, select, slot) — the same source of truth used
   * by the display label and the past/upcoming ribbon.
   */
  const sortedProducts = React.useMemo(() => {
    return [...products].sort((a, b) => {
      const aTs = getEventStart(a)?.getTime() ?? null;
      const bTs = getEventStart(b)?.getTime() ?? null;

      if (aTs === null && bTs === null) return 0;
      if (aTs === null) return 1;
      if (bTs === null) return -1;

      return bTs - aTs;
    });
  }, [products]);

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / PAGE_SIZE));
  const current = Math.max(1, Math.min(page, totalPages));
  const pages = totalPages > 1 ? pageWindow(current, totalPages, 5) : [];

  const pagedProducts = React.useMemo(
    () => sortedProducts.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE),
    [sortedProducts, current]
  );

  const goToPage = React.useCallback(
    (p: number) => {
      const next = Math.max(1, Math.min(p, totalPages));
      shouldScrollRef.current = true;
      if (next === page) {
        scrollToGridWithOffset();
        shouldScrollRef.current = false;
        return;
      }
      setPage(next);
    },
    [page, totalPages, scrollToGridWithOffset]
  );

  const showGridSkeleton = loading && sortedProducts.length === 0;

  const ribbonLabels = {
    free: t("free_label", "Gratuito"),
    soldOut: t("sold_out_label", "Esgotado"),
    past: t("past_label", "Concluído"),
  };

  const renderCard = (product: Product) => {
    const href = getHref(product);
    const title = getTitle(product);
    const imgSrc = getImageSrc(product);
    const imgAlt = product._embedded?.["wp:featuredmedia"]?.[0]?.alt_text || title;
    const { html: excerptHtml, generated } = getExcerptLike(product);
    const eventDateLabel = getEventDateLabel(product, lang);
    const ribbon = getRibbonSpec(product, eventDateLabel, ribbonLabels);

    return (
      <article key={product.id}>
        {imgSrc && (
          <Link href={href} className="block group">
            <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
              {ribbon && (
                <div className={`price-ribbon price-ribbon--sm${ribbon.modifier ? " " + ribbon.modifier : ""}`}>
                  <span className="price-ribbon__label">{ribbon.label}</span>
                </div>
              )}
              <Image
                src={imgSrc}
                alt={imgAlt}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
              />
            </div>
          </Link>
        )}
        <div className="py-4">
          <h3 className="m-0 mb-1">
            <Link href={href} className="hover:underline">{title}</Link>
          </h3>
          {eventDateLabel && <div className="text-sm text-gray-600 mb-2">{eventDateLabel}</div>}
          {excerptHtml ? (
            <div className="leading-6 text-gray-900 line-clamp-2 [&_p]:m-0 [&_*]:m-0">
              <SafeHtml content={excerptHtml} />
            </div>
          ) : generated ? (
            <div className="leading-6 m-0 line-clamp-2 text-gray-900">
              <SafeHtml content={generated} />
            </div>
          ) : null}
        </div>
      </article>
    );
  };

  return (
    <Section id="shop-grid">
      <Wrapper>
        <nav className="mb-12 flex justify-center" aria-label={stripHtml(t("filter_aria_label", "Filtrar por categoria"))}>
          <ul
            ref={trackRef}
            role="tablist"
            className="relative inline-flex items-center gap-1 rounded-full bg-gray-100 p-1 max-w-full overflow-x-auto whitespace-nowrap list-none m-0"
          >
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 z-0 rounded-full bg-white shadow-sm transition-[transform,width,height] duration-150 ease-out will-change-transform pointer-events-none"
              style={{
                transform: `translate(${slider.left}px, ${slider.top}px)`,
                width: `${slider.width}px`,
                height: `${slider.height}px`,
              }}
            />
            {tabItems.map((item) => {
              const selected = activeCat === item.slug;
              return (
                <li key={item.slug} className="relative m-0">
                  <button
                    type="button"
                    ref={setTabRef(item.slug)}
                    role="tab"
                    aria-selected={selected}
                    onClick={() => selectCat(item.slug)}
                    className={[
                      "relative z-10 inline-flex items-center justify-center h-9 px-4 text-sm font-semibold rounded-full bg-transparent border-0 cursor-pointer",
                      "no-underline hover:no-underline focus:no-underline transition-colors duration-150",
                      selected ? "text-gray-900" : "text-gray-600 hover:text-gray-900",
                    ].join(" ")}
                  >
                    <SafeHtml content={item.name} />
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div ref={gridTopRef} />

        {sortedProducts.length === 0 && !loading ? (
          <div className="text-center m-0">
            <SafeHtml content={t("no_products_message", "Nenhum produto encontrado.")} />
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-busy={loading || undefined}>
            {showGridSkeleton
              ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="w-full aspect-[4/3] bg-gray-100 mb-4" />
                  <div className="h-4 bg-gray-100 w-3/4 mb-2" />
                  <div className="h-3 bg-gray-100 w-1/2" />
                </div>
              ))
              : pagedProducts.map((product) => renderCard(product))}
          </div>
        )}

        {totalPages > 1 && !showGridSkeleton ? (
          <nav className="mt-12 flex flex-wrap items-center justify-center gap-6" aria-label="Pagination">
            <button
              type="button"
              disabled={current <= 1}
              onClick={() => goToPage(current - 1)}
              className="px-2 py-1 disabled:opacity-40"
            >
              Prev
            </button>

            {pages.map((p) => {
              const isCurrent = p === current;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => goToPage(p)}
                  aria-current={isCurrent ? "page" : undefined}
                  className={`px-2 py-1 ${isCurrent ? "underline underline-offset-4" : ""}`}
                >
                  {p}
                </button>
              );
            })}

            <button
              type="button"
              disabled={current >= totalPages}
              onClick={() => goToPage(current + 1)}
              className="px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </nav>
        ) : null}
      </Wrapper>
    </Section>
  );
}